import {Color} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {FONT_FAMILY_PROXIMA} from '../../styles/FontConstants';
import MenuPanelRow from './MenuPanelRow';

export interface MenuPanelSection {
  header?: string;
  rows: Array<{
    id: string;
    label: string;
    description?: string;
    badge?: JSX.Element;
    isDisabled?: boolean;
  }>;
}

interface BaseMenuPanelProps {
  sections: Array<MenuPanelSection>;
  showDescriptions?: boolean;
}

type MenuPanelProps =
  | (BaseMenuPanelProps & {
      selectedId?: never;
      onSelectId?: never;
    })
  | (BaseMenuPanelProps & {
      selectedId: string | null;
      onSelectId: (id: string) => void;
    });

export default function MenuPanel({
  sections,
  showDescriptions,
  selectedId,
  onSelectId,
}: MenuPanelProps): React.ReactElement {
  return (
    <div className={css(styles.container)}>
      {sections.map((section, sectionIndex) => (
        <div key={sectionIndex} className={css(styles.section)}>
          {section.header && <div className={css(styles.header)}>{section.header}</div>}
          <div className={css(styles.rows)}>
            {section.rows.map((row) => (
              <MenuPanelRow
                key={row.id}
                label={row.label}
                description={showDescriptions ? (row.description ?? null) : null}
                isSelected={selectedId === row.id}
                isDisabled={row.isDisabled}
                onClick={() => onSelectId?.(row.id)}
                badge={row.badge}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '3px',
    border: `1px solid ${Color.GRAY_300}`,
    overflowY: 'auto',
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: 'auto',
    minHeight: 0,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    backgroundColor: Color.GRAY_100,
    fontFamily: FONT_FAMILY_PROXIMA,
    fontSize: '13px',
    fontWeight: 'bold',
    padding: '7px 8px',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  rows: {
    display: 'flex',
    flexDirection: 'column',
  },
});
